import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  Receipt,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Pencil,
  Trash2,
  Save,
  X,
  Banknote,
  Copy,
  Repeat,
  ArrowDownUp,
} from 'lucide-react';
import { useStore } from '../store/useStore';
import { apiClient } from '../lib/apiClient';
import { formatDate, formatCurrency, INVOICE_STATUS_CONFIG, overdueStatusUpdates } from '../lib/utils';
import { projectValueAfter, shareOfProjectValue } from '../lib/projectValue';
import { InvoiceBadge, StageBadge } from '../components/Badge';
import Modal from '../components/Modal';
import QuickPayModal from '../components/QuickPayModal';
import type { InvoiceMilestone, InvoiceStatus } from '../types';

// ── Month helpers (UTC-safe: no timezone drift on ISO conversion) ────────────
const ET_MONTHS = ['Jaanuar', 'Veebruar', 'Märts', 'Aprill', 'Mai', 'Juuni', 'Juuli', 'August', 'September', 'Oktoober', 'November', 'Detsember'];

function monthDateStr(year: number, monthIdx: number, day: number | 'end'): string {
  const lastDay = new Date(Date.UTC(year, monthIdx + 1, 0)).getUTCDate();
  const d = day === 'end' ? lastDay : Math.min(day, lastDay);
  return new Date(Date.UTC(year, monthIdx, d)).toISOString().split('T')[0];
}

function monthTitle(year: number, monthIdx: number): string {
  return `${ET_MONTHS[monthIdx]} ${year}`;
}

/** Shift an invoice's planned date one month forward. A last-day-of-month date
 *  stays on the last day; otherwise the day-of-month is kept (clamped). */
function nextMonthDate(dateStr: string): string {
  const d = new Date(dateStr.split('T')[0] + 'T00:00:00Z');
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const day = d.getUTCDate();
  const wasLastDay = day === new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  const ny = m === 11 ? y + 1 : y;
  const nm = m === 11 ? 0 : m + 1;
  return monthDateStr(ny, nm, wasLastDay ? 'end' : day);
}

/** If the title is an Estonian month name (with optional year), advance it to
 *  match the new date; otherwise keep the original title. */
function nextMonthTitle(title: string, newDateStr: string): string {
  const d = new Date(newDateStr + 'T00:00:00Z');
  const m = title.trim().match(/^([A-Za-zÕÄÖÜõäöü]+)(\s+\d{4})?$/);
  if (m && ET_MONTHS.some((n) => n.toLowerCase() === m[1].toLowerCase())) {
    return m[2] ? monthTitle(d.getUTCFullYear(), d.getUTCMonth()) : ET_MONTHS[d.getUTCMonth()];
  }
  return title;
}

function InvoiceCard({
  invoice,
  stageName,
  projectValue,
  invoicedTotal,
  onUpdate,
  onDelete,
  onDuplicate,
}: {
  invoice: InvoiceMilestone;
  stageName: string;
  projectValue: number;
  invoicedTotal: number;
  onUpdate: (updates: Partial<InvoiceMilestone>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const toDateStr = (d: string | null | undefined) => d?.split('T')[0] ?? '';
  const [form, setForm] = useState({
    ...invoice,
    plannedSendDate: toDateStr(invoice.plannedSendDate),
    sentDate: toDateStr(invoice.sentDate),
    paidDate: toDateStr(invoice.paidDate),
    dueDate: invoice.dueDate ? toDateStr(invoice.dueDate) : undefined,
  });

  const inputCls = 'px-2 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500';

  // Saving a changed amount moves the project total with it.
  const editedValue = projectValueAfter(projectValue, invoicedTotal, {
    from: Number(invoice.amount),
    to: Number(form.amount),
  });

  const STATUS_OPTIONS: { value: InvoiceStatus; label: string }[] = [
    { value: 'not_ready', label: 'Not Ready' },
    { value: 'ready_to_send', label: 'Ready to Send' },
    { value: 'sent', label: 'Sent' },
    { value: 'paid', label: 'Paid' },
    { value: 'overdue', label: 'Overdue' },
  ];

  if (editing) {
    return (
      <div className="bg-blue-50/60 border border-blue-100 rounded-xl p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Title</label>
            <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className={`${inputCls} w-full`} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Invoice Number</label>
            <input value={form.invoiceNumber ?? ''} onChange={(e) => setForm((f) => ({ ...f, invoiceNumber: e.target.value }))} className={`${inputCls} w-full`} placeholder="auto-generated" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Amount (€)</label>
            <input type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: Number(e.target.value) }))} className={`${inputCls} w-full`} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">VAT %</label>
            <input type="number" min={0} max={100} value={Number(form.vatRate ?? 24)} onChange={(e) => setForm((f) => ({ ...f, vatRate: Number(e.target.value) }))} className={`${inputCls} w-full`} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">% of project</label>
            <p className={`${inputCls} w-full bg-gray-50 text-gray-500`}>{shareOfProjectValue(Number(form.amount), editedValue)}%</p>
            {editedValue !== projectValue && (
              <p className="text-[11px] text-gray-400 mt-1">Total project value → {formatCurrency(editedValue)}</p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Planned Send</label>
            <input type="date" value={form.plannedSendDate} onChange={(e) => setForm((f) => ({ ...f, plannedSendDate: e.target.value }))} className={`${inputCls} w-full`} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Due Date</label>
            <input type="date" value={form.dueDate ?? ''} onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} className={`${inputCls} w-full`} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Sent Date</label>
            <input type="date" value={form.sentDate} onChange={(e) => setForm((f) => ({ ...f, sentDate: e.target.value }))} className={`${inputCls} w-full`} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Paid Date</label>
            <input type="date" value={form.paidDate} onChange={(e) => setForm((f) => ({ ...f, paidDate: e.target.value }))} className={`${inputCls} w-full`} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Status</label>
            <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as InvoiceStatus }))} className={`${inputCls} w-full`}>
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Notes</label>
            <input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className={`${inputCls} w-full`} />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={() => setEditing(false)} className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50">
            <X size={12} /> Cancel
          </button>
          <button onClick={() => { onUpdate(form); setEditing(false); }} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-brand-500 text-white rounded-lg hover:bg-brand-600">
            <Save size={12} /> Save
          </button>
        </div>
      </div>
    );
  }

  const cfg = INVOICE_STATUS_CONFIG[invoice.status];

  return (
    <div className={`rounded-xl border p-5 group ${
      invoice.status === 'overdue' ? 'border-red-200 bg-red-50/30' :
      invoice.status === 'paid' ? 'border-green-100 bg-green-50/20' :
      invoice.status === 'ready_to_send' ? 'border-yellow-200 bg-yellow-50/20' :
      'border-gray-100 bg-white'
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-semibold text-gray-900">{invoice.title}</span>
            {invoice.invoiceNumber && (
              <span className="text-xs text-gray-400 font-mono bg-gray-100 px-1.5 py-0.5 rounded">{invoice.invoiceNumber}</span>
            )}
            <InvoiceBadge status={invoice.status} />
            {stageName && (
              <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                {stageName}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs text-gray-500">
            <span>
              <span className="text-gray-400">Planned send:</span>{' '}
              <span className={invoice.status === 'overdue' ? 'text-red-600 font-medium' : ''}>
                {formatDate(invoice.plannedSendDate)}
              </span>
            </span>
            {invoice.dueDate && (
              <span className={new Date(invoice.dueDate) < new Date() && invoice.status !== 'paid' ? 'text-red-500 font-medium' : ''}>
                <span className="text-gray-400">Due:</span> {formatDate(invoice.dueDate)}
              </span>
            )}
            {invoice.sentDate && (
              <span><span className="text-gray-400">Sent:</span> {formatDate(invoice.sentDate)}</span>
            )}
            {invoice.paidDate && (
              <span><span className="text-gray-400">Paid:</span> {formatDate(invoice.paidDate)}</span>
            )}
          </div>
          {invoice.notes && (
            <p className="mt-1.5 text-xs text-gray-400 italic">{invoice.notes}</p>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="text-xl font-bold text-gray-900">{formatCurrency(Number(invoice.amount))}</div>
          {Number(invoice.vatRate) > 0 && (
            <div className="text-xs text-gray-400">
              + VAT {invoice.vatRate}% = {formatCurrency(Number(invoice.amount) * (1 + Number(invoice.vatRate) / 100))}
            </div>
          )}
          <div className="text-xs text-gray-400">{shareOfProjectValue(Number(invoice.amount), projectValue)}% of project</div>
        </div>
        <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <button onClick={() => setEditing(true)} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg" title="Edit">
            <Pencil size={12} />
          </button>
          <button onClick={onDuplicate} className="p-1.5 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg" title="Duplicate for next month">
            <Copy size={12} />
          </button>
          <button onClick={onDelete} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Delete">
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      {/* Quick status change buttons */}
      <div className="mt-4 pt-3 border-t border-gray-100 flex gap-2 flex-wrap">
        {invoice.status === 'ready_to_send' && (
          <button
            onClick={() => onUpdate({ status: 'sent', sentDate: new Date().toISOString().split('T')[0] })}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium"
          >
            <Receipt size={12} /> Mark as Sent
          </button>
        )}
        {invoice.status === 'not_ready' && (
          <button
            onClick={() => onUpdate({ status: 'ready_to_send' })}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 font-medium"
          >
            Mark Ready to Send
          </button>
        )}
        {invoice.status === 'sent' && (
          <button
            onClick={() => onUpdate({ status: 'paid', paidDate: new Date().toISOString().split('T')[0] })}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium"
          >
            <CheckCircle2 size={12} /> Mark as Paid
          </button>
        )}
        {(invoice.status === 'sent' || invoice.status === 'ready_to_send') && (
          <button
            onClick={() => onUpdate(overdueStatusUpdates(invoice))}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-600 border border-red-200 bg-red-50 rounded-lg hover:bg-red-100 font-medium"
            title="Marks the invoice overdue (sets due date to yesterday if it has none)"
          >
            <AlertTriangle size={12} /> Mark Overdue
          </button>
        )}
      </div>
    </div>
  );
}

function AddInvoiceModal({
  projectId,
  stages,
  projectValue,
  invoicedTotal,
  onSave,
  onClose,
}: {
  projectId: string;
  stages: { id: string; title: string }[];
  projectValue: number;
  invoicedTotal: number;
  onSave: (inv: Omit<InvoiceMilestone, 'id'>) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<Omit<InvoiceMilestone, 'id'>>({
    projectId,
    relatedStageId: null,
    invoiceNumber: '',
    title: '',
    amount: 0,
    vatRate: 24,
    percentage: 0,
    plannedSendDate: '',
    sentDate: '',
    paidDate: '',
    dueDate: '',
    status: 'not_ready',
    notes: '',
  });
  const inputCls = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500';

  // A new invoice raises the project total once the invoices pass it.
  const valueAfterAdd = projectValueAfter(projectValue, invoicedTotal, { to: Number(form.amount) });

  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs text-gray-500 block mb-1">Invoice Title *</label>
        <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className={inputCls} placeholder="e.g. Design Phase Completion" />
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">Related Stage</label>
        <select value={form.relatedStageId ?? ''} onChange={(e) => setForm((f) => ({ ...f, relatedStageId: e.target.value || null }))} className={inputCls}>
          <option value="">No stage linked</option>
          {stages.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
        </select>
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">Invoice Number</label>
        <input value={form.invoiceNumber ?? ''} onChange={(e) => setForm((f) => ({ ...f, invoiceNumber: e.target.value }))} className={inputCls} placeholder="auto-generated if blank" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-xs text-gray-500 block mb-1">Amount (€) *</label>
          <input type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: Number(e.target.value) }))} className={inputCls} />
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">VAT %</label>
          <input type="number" min={0} max={100} value={form.vatRate ?? 24} onChange={(e) => setForm((f) => ({ ...f, vatRate: Number(e.target.value) }))} className={inputCls} />
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">% of project</label>
          <p className={`${inputCls} bg-gray-50 text-gray-500`}>{shareOfProjectValue(Number(form.amount), valueAfterAdd)}%</p>
          {valueAfterAdd !== projectValue && (
            <p className="text-[11px] text-gray-400 mt-1">Total project value → {formatCurrency(valueAfterAdd)}</p>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 block mb-1">Planned Send Date</label>
          <input type="date" value={form.plannedSendDate} onChange={(e) => setForm((f) => ({ ...f, plannedSendDate: e.target.value }))} className={inputCls} />
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">Due Date</label>
          <input type="date" value={form.dueDate ?? ''} onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} className={inputCls} />
        </div>
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1">Notes</label>
        <input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className={inputCls} />
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
        <button onClick={() => form.title.trim() && onSave(form)} className="px-4 py-2 text-sm bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium">Add Invoice Milestone</button>
      </div>
    </div>
  );
}

// ── Monthly series generator ─────────────────────────────────────────────────
function GenerateSeriesModal({
  projectId,
  existingInvoices,
  defaultAmount,
  onDone,
  onClose,
}: {
  projectId: string;
  existingInvoices: InvoiceMilestone[];
  defaultAmount: number;
  onDone: () => Promise<void>;
  onClose: () => void;
}) {
  const now = new Date();
  const [amount, setAmount] = useState<number>(defaultAmount);
  const [vatRate, setVatRate] = useState<number>(24);
  const [startMonth, setStartMonth] = useState(() => `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [months, setMonths] = useState<number>(12);
  const [dateMode, setDateMode] = useState<'end' | 'day'>('end');
  const [dayOfMonth, setDayOfMonth] = useState<number>(15);
  const [skipExisting, setSkipExisting] = useState(true);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [error, setError] = useState('');

  // Months (yyyy-MM) that already have at least one invoice
  const occupiedMonths = useMemo(() => {
    const s = new Set<string>();
    for (const inv of existingInvoices) {
      if (inv.plannedSendDate) s.add(inv.plannedSendDate.split('T')[0].slice(0, 7));
    }
    return s;
  }, [existingInvoices]);

  // Build the list this config would create
  const planned = useMemo(() => {
    const [sy, sm] = startMonth.split('-').map(Number);
    if (!sy || !sm || months < 1) return [];
    const list: { title: string; date: string; skipped: boolean }[] = [];
    for (let i = 0; i < Math.min(months, 120); i++) {
      const y = sy + Math.floor((sm - 1 + i) / 12);
      const m = (sm - 1 + i) % 12;
      const date = monthDateStr(y, m, dateMode === 'end' ? 'end' : dayOfMonth);
      const skipped = skipExisting && occupiedMonths.has(date.slice(0, 7));
      list.push({ title: monthTitle(y, m), date, skipped });
    }
    return list;
  }, [startMonth, months, dateMode, dayOfMonth, skipExisting, occupiedMonths]);

  const toCreate = planned.filter((p) => !p.skipped);
  const canGenerate = amount > 0 && toCreate.length > 0 && !progress;

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setError('');
    setProgress({ done: 0, total: toCreate.length });
    try {
      for (let i = 0; i < toCreate.length; i++) {
        await apiClient.post('/invoices', {
          projectId,
          relatedStageId: null,
          title: toCreate[i].title,
          amount,
          vatRate,
          percentage: 0,
          plannedSendDate: toCreate[i].date,
          status: 'not_ready',
          notes: '',
        });
        setProgress({ done: i + 1, total: toCreate.length });
      }
      await onDone();
      onClose();
    } catch (e) {
      setError((e as Error)?.message ?? 'Failed to create invoices.');
      setProgress(null);
    }
  };

  const inputCls = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500';

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 block mb-1">Amount per invoice (€) *</label>
          <input type="number" value={amount || ''} onChange={(e) => setAmount(Number(e.target.value))} className={inputCls} />
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">VAT %</label>
          <input type="number" min={0} max={100} value={vatRate} onChange={(e) => setVatRate(Number(e.target.value))} className={inputCls} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 block mb-1">First month</label>
          <input type="month" value={startMonth} onChange={(e) => setStartMonth(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">Number of months</label>
          <input type="number" min={1} max={120} value={months} onChange={(e) => setMonths(Number(e.target.value))} className={inputCls} />
        </div>
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-1.5">Invoice date</label>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
            <input type="radio" checked={dateMode === 'end'} onChange={() => setDateMode('end')} />
            Last day of month
          </label>
          <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
            <input type="radio" checked={dateMode === 'day'} onChange={() => setDateMode('day')} />
            Specific day:
          </label>
          <input
            type="number"
            min={1}
            max={31}
            value={dayOfMonth}
            disabled={dateMode !== 'day'}
            onChange={(e) => setDayOfMonth(Math.min(31, Math.max(1, Number(e.target.value) || 1)))}
            className={`${inputCls} w-20 disabled:opacity-40`}
          />
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
        <input type="checkbox" checked={skipExisting} onChange={(e) => setSkipExisting(e.target.checked)} className="rounded" />
        Skip months that already have an invoice
      </label>

      {/* Preview */}
      <div className="bg-gray-50 border border-gray-100 rounded-lg px-4 py-3 text-xs text-gray-600 space-y-1">
        <p>
          <span className="font-semibold text-gray-800">{toCreate.length}</span> invoice{toCreate.length !== 1 ? 's' : ''} will be created
          {planned.length > toCreate.length && <span className="text-gray-400"> ({planned.length - toCreate.length} month{planned.length - toCreate.length !== 1 ? 's' : ''} skipped)</span>}
          {toCreate.length > 0 && <> · total <span className="font-semibold text-gray-800">{formatCurrency(toCreate.length * (amount || 0))}</span> + VAT</>}
        </p>
        {toCreate.length > 0 && (
          <p className="text-gray-400">
            {toCreate[0].title} ({formatDate(toCreate[0].date)}) → {toCreate[toCreate.length - 1].title} ({formatDate(toCreate[toCreate.length - 1].date)})
          </p>
        )}
      </div>

      {error && <p className="text-xs text-red-500">{error}</p>}

      <div className="flex items-center justify-end gap-2 pt-1">
        {progress && (
          <span className="text-xs text-gray-400 mr-auto">Creating {progress.done}/{progress.total}…</span>
        )}
        <button onClick={onClose} disabled={!!progress} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">Cancel</button>
        <button
          onClick={handleGenerate}
          disabled={!canGenerate}
          className="flex items-center gap-1.5 px-4 py-2 text-sm bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium disabled:opacity-50"
        >
          <Repeat size={13} /> Generate {toCreate.length > 0 ? toCreate.length : ''} invoice{toCreate.length !== 1 ? 's' : ''}
        </button>
      </div>
    </div>
  );
}

export default function InvoiceTracking() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const projects = useStore((s) => s.projects);
  const clients = useStore((s) => s.clients);
  const invoices = useStore((s) => s.invoices);
  const stages = useStore((s) => s.stages);
  const updateInvoice = useStore((s) => s.updateInvoice);
  const deleteInvoice = useStore((s) => s.deleteInvoice);
  const addInvoice = useStore((s) => s.addInvoice);

  const [showAdd, setShowAdd] = useState(false);
  const [showSeries, setShowSeries] = useState(false);
  const [showQuickPay, setShowQuickPay] = useState(false);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const project = projects.find((p) => p.id === projectId);
  const client = clients.find((c) => c.id === project?.clientId);
  const projectInvoices = invoices.filter((i) => i.projectId === projectId);
  const sortedInvoices = [...projectInvoices].sort((a, b) => {
    // Invoices without a planned date always sink to the bottom.
    const da = a.plannedSendDate?.split('T')[0] ?? '9999-99-99';
    const db = b.plannedSendDate?.split('T')[0] ?? '9999-99-99';
    if (da === db) return 0;
    if (da === '9999-99-99') return 1;
    if (db === '9999-99-99') return -1;
    return sortDir === 'asc' ? da.localeCompare(db) : db.localeCompare(da);
  });
  const projectStages = stages.filter((s) => s.projectId === projectId).sort((a, b) => a.order - b.order);

  if (!project) {
    return <div className="p-8 text-center text-gray-400">Project not found.</div>;
  }

  const projectValue  = Number(project.value);
  const paidAmount    = projectInvoices.filter((i) => i.status === 'paid').reduce((sum, i) => sum + Number(i.amount), 0);
  const sentAmount    = projectInvoices.filter((i) => i.status === 'sent').reduce((sum, i) => sum + Number(i.amount), 0);
  const overdueAmount = projectInvoices.filter((i) => i.status === 'overdue').reduce((sum, i) => sum + Number(i.amount), 0);
  const overdueCount  = projectInvoices.filter((i) => i.status === 'overdue').length;
  const totalTracked  = projectInvoices.reduce((sum, i) => sum + Number(i.amount), 0);
  const untracked     = Math.max(0, projectValue - totalTracked);

  const paidPct    = projectValue ? Math.min(100, (paidAmount   / projectValue) * 100) : 0;
  const sentPct    = projectValue ? Math.min(100 - paidPct, (sentAmount   / projectValue) * 100) : 0;
  const overduePct = projectValue ? Math.min(100 - paidPct - sentPct, (overdueAmount / projectValue) * 100) : 0;

  const getStage = (id: string | null) => id ? projectStages.find((s) => s.id === id) : undefined;

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <button onClick={() => navigate(`/projects/${projectId}`)} className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 mb-6 transition-colors">
        <ArrowLeft size={14} /> {project.name}
      </button>

      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoice Tracking</h1>
          <p className="text-gray-400 text-sm mt-0.5">{client?.companyName} · {project.name}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowQuickPay(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
          >
            <Banknote size={15} /> Record Payment
          </button>
          <button
            onClick={() => setShowSeries(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
          >
            <Repeat size={15} /> Monthly Series
          </button>
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
          >
            <Plus size={15} /> Add Milestone
          </button>
        </div>
      </div>

      {/* Total project value summary */}
      <div className="bg-white rounded-xl border border-gray-100 p-5 mb-6">
        <div className="flex items-end justify-between mb-4">
          <div>
            <p className="text-xs text-gray-400 mb-0.5">Total project value</p>
            <p className="text-3xl font-bold text-gray-900">{formatCurrency(projectValue)}</p>
          </div>
          <p className="text-sm text-gray-400">
            {Math.round(paidPct)}% received
          </p>
        </div>
        {/* Progress bar */}
        <div className="h-4 bg-gray-100 rounded-full overflow-hidden flex mb-3">
          <div className="bg-green-400 h-full transition-all duration-500" style={{ width: `${paidPct}%` }} />
          <div className="bg-blue-300 h-full transition-all duration-500" style={{ width: `${sentPct}%` }} />
          <div className="bg-red-300 h-full transition-all duration-500" style={{ width: `${overduePct}%` }} />
        </div>
        {/* Breakdown row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-0.5">
              <div className="w-2.5 h-2.5 rounded-full bg-green-400 shrink-0" /> Paid
            </div>
            <p className="text-sm font-semibold text-green-600">{formatCurrency(paidAmount)}</p>
          </div>
          <div>
            <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-0.5">
              <div className="w-2.5 h-2.5 rounded-full bg-blue-300 shrink-0" /> Invoiced / awaiting
            </div>
            <p className="text-sm font-semibold text-blue-600">{formatCurrency(sentAmount)}</p>
          </div>
          {overdueAmount > 0 && (
            <div>
              <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-0.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-300 shrink-0" /> Overdue
              </div>
              <p className="text-sm font-semibold text-red-500">{formatCurrency(overdueAmount)}</p>
            </div>
          )}
          <div>
            <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-0.5">
              <div className="w-2.5 h-2.5 rounded-full bg-gray-200 shrink-0" /> Still to invoice
            </div>
            <p className="text-sm font-semibold text-gray-500">{formatCurrency(untracked)}</p>
          </div>
        </div>
        {overdueCount > 0 && (
          <div className="mt-3 flex items-center gap-2 text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
            <AlertTriangle size={13} /> {overdueCount} overdue milestone{overdueCount > 1 ? 's' : ''} — action required
          </div>
        )}
      </div>

      {/* Invoice list */}
      {projectInvoices.length > 0 && (
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-gray-400">{projectInvoices.length} invoices</p>
          <button
            onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            <ArrowDownUp size={12} />
            {sortDir === 'asc' ? 'Oldest first' : 'Newest first'}
          </button>
        </div>
      )}
      <div className="space-y-4">
        {projectInvoices.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
            <Receipt size={40} className="mx-auto mb-3 text-gray-200" />
            <p className="text-gray-400 text-sm">No invoice milestones defined.</p>
          </div>
        )}
        {sortedInvoices.map((invoice) => {
          const stage = getStage(invoice.relatedStageId);
          return (
            <InvoiceCard
              key={invoice.id}
              invoice={invoice}
              stageName={stage?.title ?? ''}
              projectValue={projectValue}
              invoicedTotal={totalTracked}
              onUpdate={(updates) => updateInvoice(invoice.id, updates)}
              onDelete={() => deleteInvoice(invoice.id)}
              onDuplicate={() => {
                const newDate = invoice.plannedSendDate
                  ? nextMonthDate(invoice.plannedSendDate)
                  : new Date().toISOString().split('T')[0];
                addInvoice({
                  projectId: invoice.projectId,
                  relatedStageId: invoice.relatedStageId ?? null,
                  title: nextMonthTitle(invoice.title, newDate),
                  amount: Number(invoice.amount),
                  vatRate: Number(invoice.vatRate ?? 24),
                  percentage: Number(invoice.percentage) || 0,
                  plannedSendDate: newDate,
                  sentDate: '',
                  paidDate: '',
                  status: 'not_ready',
                  notes: invoice.notes ?? '',
                });
              }}
            />
          );
        })}
      </div>

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add Invoice Milestone" size="md">
        <AddInvoiceModal
          projectId={project.id}
          stages={projectStages.map((s) => ({ id: s.id, title: s.title }))}
          projectValue={projectValue}
          invoicedTotal={totalTracked}
          onSave={(inv) => { addInvoice(inv); setShowAdd(false); }}
          onClose={() => setShowAdd(false)}
        />
      </Modal>

      <Modal open={showSeries} onClose={() => setShowSeries(false)} title="Generate Monthly Invoice Series" size="md">
        <GenerateSeriesModal
          projectId={project.id}
          existingInvoices={projectInvoices}
          defaultAmount={projectInvoices.length ? Number(projectInvoices[projectInvoices.length - 1].amount) : 0}
          onDone={() => useStore.getState().refetchProjects()}
          onClose={() => setShowSeries(false)}
        />
      </Modal>

      <QuickPayModal
        open={showQuickPay}
        projectName={project.name}
        projectValue={projectValue}
        paidSoFar={paidAmount}
        projectId={project.id}
        onSave={(inv) => { addInvoice(inv); setShowQuickPay(false); }}
        onClose={() => setShowQuickPay(false)}
      />
    </div>
  );
}
